import React, { useState } from "react";
import toast from "react-hot-toast";
import ListSectionTemplate from "../../common/ListSectionTemplate";
import { useMobileContext } from "../../../context/MobileContext";
import { useProfile, isOwner } from "../../../context/ProfileContext";
import { deleteAccount, listMembers, renameOrg } from "../../../api/studioAPI";
import { colors, surfaces, buttons, form } from "../../../styles/index";

// The signed-in person and their org; an owner can rename the org; anyone but a demo or the platform admin can delete
// their account (backend routers/user_api.py: their plans stay with the org - or, the last one in it, the org goes too).
export default function AccountPanel({ logout }) {
  const isMobile = useMobileContext();
  const { profile, refresh } = useProfile();
  const [name, setName] = useState(profile?.org?.org_name || "");
  const [saving, setSaving] = useState(false);
  if (!profile) return null;

  const canRename = isOwner(profile) && !profile.org?.is_demo;
  const save = async () => {
    setSaving(true);
    try {
      await renameOrg(name.trim());
      toast.success("Organization renamed");
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ label, value }) => (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", gap: isMobile ? 4 : 12,
      padding: "8px 0", fontSize: "0.95rem", borderBottom: `1px solid ${colors.border}` }}>
      <div style={{ color: colors.textMuted }}>{label}</div>
      <div style={{ fontWeight: 500, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );

  return (
    <ListSectionTemplate title="Account">
      <div style={{ ...surfaces.card, textAlign: "left" }}>
        <Row label="Email" value={profile.email} />
        <Row label="Role" value={{ admin: "Platform admin", owner: "Owner", creator: "Creator" }[profile.role]} />
        <Row label="Organization" value={profile.org?.org_name} />
        <Row label="Organization ID" value={profile.org?.org_id} />
      </div>
      {canRename && (
        <form style={{ ...surfaces.card, textAlign: "left", marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) save(); }}>
          <label style={{ flex: "1 1 240px" }}>
            <span style={form.label}>Organization name</span>
            <input style={form.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} disabled={saving} />
          </label>
          <button type="submit" style={buttons.primary} disabled={saving || !name.trim() || name.trim() === profile.org?.org_name}>
            Rename
          </button>
        </form>
      )}
      {!profile.is_demo && profile.role !== "admin" && <DeleteAccount logout={logout} />}
    </ListSectionTemplate>
  );
}

function DeleteAccount({ logout }) {
  const [open, setOpen] = useState(false);
  const [alone, setAlone] = useState(null);           // the only member: the org goes too
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const start = () => {
    setOpen(true);
    listMembers().then((d) => setAlone(d.items.length <= 1)).catch(() => setAlone(false));
  };
  const remove = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const out = await deleteAccount(password);
      toast.success(out.org_deleted ? "Your account and organization were deleted" : "Your account was deleted");
      if (logout) logout("/");
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  return (
    <div style={{ ...surfaces.card, textAlign: "left", marginTop: 16, borderColor: colors.danger }}>
      <h4 style={{ margin: "0 0 6px" }}>Delete my account</h4>
      {!open ? (
        <>
          <p style={{ margin: "0 0 12px", color: colors.textMuted, fontSize: "0.9rem" }}>
            Your plans stay with the organization. If you&rsquo;re the only owner, make someone else an owner first.
          </p>
          <button type="button" style={buttons.secondary} onClick={start}>Delete my account…</button>
        </>
      ) : (
        <form onSubmit={remove} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <p role="alert" style={{ flexBasis: "100%", margin: 0, color: colors.danger, fontSize: "0.9rem" }}>
            {alone
              ? "You're the only person in this organization, so it will be deleted too - every plan, simulation, learner result and uploaded document. This can't be undone."
              : "Your account is deleted and you're signed out. Your plans stay with the organization. This can't be undone."}
          </p>
          <label style={{ flex: "1 1 220px" }}>
            <span style={form.label}>Your password, to confirm</span>
            <input type="password" autoComplete="current-password" style={form.input} value={password}
              onChange={(e) => setPassword(e.target.value)} disabled={busy} />
          </label>
          <button type="submit" style={buttons.danger} disabled={busy || !password || alone === null}>
            {alone ? "Delete my account and organization" : "Delete my account"}
          </button>
          <button type="button" style={buttons.secondary} disabled={busy} onClick={() => { setOpen(false); setPassword(""); }}>Cancel</button>
        </form>
      )}
    </div>
  );
}
